import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ChapterEnum } from 'gql/graphql';
import { WorkOrderPriceTypeEnum } from 'gql/graphql';

import { useEffect, useMemo } from 'react';

import { uerWorkOrderChangeUerRemaindersGql } from '../../WorkOrderChangeQueries';

interface IUseUerWorkOrderChangeUerRemainders {
    chapter: ChapterEnum;
    workOrderChangeId: number;
    workOrderChangePriceId?: number;
    workOrderPriceId?: number;
    seasonFromYear?: number;
}

export interface IMoveVolumesItem {
    type: WorkOrderPriceTypeEnum;
    code: string;
    remainder: number;
    id: number;
    workOrderPriceId: number;
    workOrderChangePriceId: number;
}

const requestId = 'uerWorkOrderChangeUerRemainders';

export const useUerWorkOrderChangeUerRemainders = (props: IUseUerWorkOrderChangeUerRemainders) => {
    const { chapter, workOrderChangePriceId, workOrderPriceId, workOrderChangeId, seasonFromYear } = props;

    const [uerWorkOrderChangeUerRemainders] = useResetStoreData({ requestId });

    const variables = useMemo(
        () => ({ chapter, workOrderChangeId, workOrderChangePriceId, workOrderPriceId }),
        [chapter, workOrderChangeId, workOrderChangePriceId, workOrderPriceId],
    );

    const [uerWorkOrderChangeUerRemaindersData, uerWorkOrderChangeUerRemaindersStatus] = useRequest({
        requestId,
        variables,
        query: uerWorkOrderChangeUerRemaindersGql,
    });

    const items: IMoveVolumesItem[] = useMemo(
        () =>
            uerWorkOrderChangeUerRemaindersData?.uerWorkOrderChangeUerRemainders?.map((i) => {
                const remainder = seasonFromYear
                    ? i.reminderData.find((r) => r.seasonYear === seasonFromYear)?.remainderVolume || 0
                    : 0;

                return {
                    id: i.clientId,
                    workOrderPriceId: i.workOrderPriceId,
                    workOrderChangePriceId: i.workOrderChangePriceId,
                    type: i.type,
                    code: i.code,
                    remainder,
                    volume: 0,
                };
            }) || [],
        [seasonFromYear, uerWorkOrderChangeUerRemaindersData?.uerWorkOrderChangeUerRemainders],
    );

    const coefficientsIds = useMemo(
        () =>
            items
                .filter((i) =>
                    [
                        WorkOrderPriceTypeEnum.Coefficient,
                        WorkOrderPriceTypeEnum.ConstraintCoefficient,
                        WorkOrderPriceTypeEnum.TechPartCoefficient,
                    ].includes(i.type),
                )
                .map((i) => i.id),
        [items],
    );

    const mtrIds = useMemo(
        () =>
            items
                .filter((i) =>
                    [
                        WorkOrderPriceTypeEnum.Mtr,
                        WorkOrderPriceTypeEnum.MtrContractorEquipment,
                        WorkOrderPriceTypeEnum.MtrContractorMaterial,
                        WorkOrderPriceTypeEnum.MtrCustomerEquipment,
                        WorkOrderPriceTypeEnum.MtrCustomerMaterial,
                    ].includes(i.type),
                )
                .map((i) => i.id),
        [items],
    );

    const volumesFieldsIds = useMemo(() => items?.map((i) => `volume_${i.id}`), [items]);

    useEffect(() => {
        uerWorkOrderChangeUerRemainders(undefined);
    }, [uerWorkOrderChangeUerRemainders]);

    return {
        status: uerWorkOrderChangeUerRemaindersStatus,
        items,
        coefficientsIds,
        volumesFieldsIds,
        mtrIds,
    };
};
